import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { convertFromUTC } from "utils/utcConversion";
import { theme } from "styles/theme";
import type { AssigneeConflict } from "./checkAppointmentConflicts";

const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";

export function countConflictAssignees(conflicts: AssigneeConflict[]): number {
  return conflicts.length;
}

export type ConflictLogEntry = {
  group: AssigneeConflict;
  appt: AssigneeConflict["appointments"][number];
};

export function flattenConflictLogs(
  conflicts: AssigneeConflict[]
): ConflictLogEntry[] {
  return conflicts.flatMap((group) =>
    group.appointments.map((appt) => ({ group, appt }))
  );
}

/** Stable id for one conflict log card (assignee + overlapping appointment). */
export function conflictLogKey(assigneeId: number, appointmentId: number): string {
  return `${assigneeId}-${appointmentId}`;
}

export function getConflictLogKeys(conflicts: AssigneeConflict[]): Set<string> {
  return new Set(
    flattenConflictLogs(conflicts).map(({ group, appt }) =>
      conflictLogKey(group.assigneeId, appt.id)
    )
  );
}

/**
 * Prompt submit confirmation when conflict logs grew or include a new distinct entry.
 * Does not prompt when count decreased or only previously seen logs remain.
 */
export function serializeConflictLogKeys(keys: Set<string>): string {
  return [...keys].sort().join(",");
}

export function shouldPromptConflictConfirmation(
  currentKeys: Set<string>,
  baselineKeys: Set<string>
): boolean {
  if (currentKeys.size === 0) return false;
  if (baselineKeys.size === 0) return true;
  if (currentKeys.size > baselineKeys.size) return true;
  for (const key of currentKeys) {
    if (!baselineKeys.has(key)) return true;
  }
  return false;
}

/**
 * Show submit confirmation when conflicts worsened vs the last submit attempt.
 * On first submit, also require conflicts to be new vs the acknowledged snapshot
 * (conflicts present when the modal opened or after the last successful save) — not
 * the live inline warning, which updates as the user edits assignees.
 */
export function shouldPromptSubmitConflictConfirmation(
  submitKeys: Set<string>,
  lastSubmitBaselineKeys: Set<string>,
  acknowledgedConflictLogKeys: Set<string>
): boolean {
  const worsenedVsLastSubmit = shouldPromptConflictConfirmation(
    submitKeys,
    lastSubmitBaselineKeys
  );
  if (!worsenedVsLastSubmit) {
    return false;
  }

  const worsenedVsAcknowledged = shouldPromptConflictConfirmation(
    submitKeys,
    acknowledgedConflictLogKeys
  );

  if (lastSubmitBaselineKeys.size === 0) {
    return worsenedVsAcknowledged;
  }

  return true;
}

type AssigneeOption = { value: number | string; label: string };

/** Keep existing chip order; append newly selected assignees at the end. */
export function preserveAssigneeSelectionOrder(
  previous: AssigneeOption[],
  next: AssigneeOption[] | null | undefined
): AssigneeOption[] {
  const selected = Array.isArray(next) ? next : [];
  if (!selected.length) return [];
  if (!previous.length) return selected;

  const byValue = new Map(selected.map((a) => [String(a.value), a]));
  const ordered: AssigneeOption[] = [];

  for (const item of previous) {
    const match = byValue.get(String(item.value));
    if (match) {
      ordered.push(match);
      byValue.delete(String(item.value));
    }
  }

  for (const item of selected) {
    if (byValue.has(String(item.value))) {
      ordered.push(item);
    }
  }

  return ordered;
}

export function assigneeSelectionsEqual(
  a: AssigneeOption[],
  b: AssigneeOption[]
): boolean {
  if (a.length !== b.length) return false;
  return a.every((item, index) => String(item.value) === String(b[index]?.value));
}

function useConflictFormatters(timezone: string) {
  const { t } = useTranslation();
  const tr = (key: string) => t(`${tBase}.${key}`);

  const formatDateTimeRange = (
    dateStr: string,
    startUtc: string,
    endUtc: string
  ) => {
    let dateLabel = dateStr;
    try {
      dateLabel = format(parseISO(dateStr), "MMM d, yyyy");
    } catch {
      // keep raw date string
    }
    const start = convertFromUTC(startUtc, timezone);
    const end = convertFromUTC(endUtc, timezone);
    return `${dateLabel}, ${start}–${end}`;
  };

  return { tr, formatDateTimeRange };
}

type ListProps = {
  conflicts: AssigneeConflict[];
  timezone: string;
  /** When set, only this many conflict logs show until expanded. */
  maxVisible?: number;
};

export function ConflictDetailList({
  conflicts,
  timezone,
  maxVisible,
}: ListProps) {
  const { tr, formatDateTimeRange } = useConflictFormatters(timezone);
  const [expanded, setExpanded] = useState(false);
  const entries = useMemo(() => flattenConflictLogs(conflicts), [conflicts]);
  const entriesKey = entries
    .map(({ group, appt }) => `${group.assigneeId}-${appt.id}`)
    .join(",");

  useEffect(() => {
    setExpanded(false);
  }, [entriesKey]);

  const showMoreControl =
    maxVisible != null && entries.length > maxVisible;
  const visibleEntries =
    showMoreControl && !expanded
      ? entries.slice(0, maxVisible)
      : entries;

  return (
    <>
      <CardList>
        {visibleEntries.map(({ group, appt }) => (
          <ConflictCard key={`${group.assigneeId}-${appt.id}`}>
            <DetailRow>
              <DetailLabel>{tr("Conflict field assignee")}:</DetailLabel>
              <DetailValue>{group.assigneeName}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>{tr("Conflict field appointment id")}:</DetailLabel>
              <DetailValue>#{appt.id}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>{tr("Conflict field date time")}:</DetailLabel>
              <DetailValue>
                {formatDateTimeRange(
                  appt.date,
                  appt.start_time_utc,
                  appt.end_time_utc
                )}
              </DetailValue>
            </DetailRow>
          </ConflictCard>
        ))}
      </CardList>
      {showMoreControl ? (
        <ShowMoreButton type="button" onClick={() => setExpanded((v) => !v)}>
          {expanded ? tr("Show less") : tr("Show more")}
        </ShowMoreButton>
      ) : null}
    </>
  );
}

export function AppointmentConflictConfirmBody({ conflicts, timezone }: ListProps) {
  const { tr } = useConflictFormatters(timezone);
  const assigneeCount = countConflictAssignees(conflicts);

  return (
    <ConfirmBody>
      <SectionTitle>{tr("Schedule conflicts")}</SectionTitle>
      <ConfirmIntro>
        {assigneeCount === 1
          ? tr("Assignee conflict confirm intro_one")
          : tr("Assignee conflict confirm intro_other")}
      </ConfirmIntro>
      <ConflictDetailList conflicts={conflicts} timezone={timezone} />
    </ConfirmBody>
  );
}

const ShowMoreButton = styled.button`
  margin-top: 0.5rem;
  padding: 0;
  border: none;
  background: none;
  font-size: ${theme.fontSize.small};
  font-weight: 600;
  color: #a16207;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #854d0e;
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ConflictCard = styled.div`
  padding: 0.5rem 0.65rem;
  border: 1px solid ${theme.border.light};
  border-radius: 4px;
  background: #fafafa;
  font-size: ${theme.fontSize.small};
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 7.5rem 1fr;
  gap: 0.25rem 0.5rem;
  align-items: baseline;

  & + & {
    margin-top: 0.2rem;
  }
`;

const DetailLabel = styled.span`
  font-weight: bold;
  color: ${theme.color.subtext2};
`;

const DetailValue = styled.span`
  color: ${theme.color.subtext};
  word-break: break-word;
`;

const ConfirmBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: ${theme.fontSize.large};
  font-weight: bold;
  color: ${theme.color.text};
  line-height: 1.3;
`;

const ConfirmIntro = styled.p`
  margin: 0 0 0.75rem;
  font-size: ${theme.fontSize.regular};
  font-weight: 500;
  color: ${theme.color.subtext};
  line-height: 1.45;
`;
